/**
 * Sentry browser SDK init.
 *
 * The DSN is publishable — Sentry expects it in client code. We do NOT enable
 * session replay (avoids PII), and we filter common browser noise via
 * beforeSend. Localhost is skipped entirely so dev errors don't burn quota.
 */

import * as Sentry from "@sentry/react";

const DSN =
  "https://6b043728bb8740c97e4228bb5c17aef5@o4511349553299456.ingest.us.sentry.io/4511349569421312";

const NOISY_MESSAGE_FRAGMENTS = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  "Failed to fetch",
  "NetworkError when attempting to fetch resource",
  "Load failed",
  "AbortError",
];

const resolveEnvironment = (): string => {
  if (typeof window === "undefined") return "ssr";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (host === "excerptus.lovable.app" || host === "excerptus.io" || host.endsWith(".excerptus.io")) {
    return "production";
  }
  if (host.endsWith(".lovable.app") || host.endsWith(".lovableproject.com")) return "preview";
  return "unknown";
};

let initialized = false;

export const initSentry = (): void => {
  if (initialized) return;
  if (typeof window === "undefined") return;

  const environment = resolveEnvironment();
  if (environment === "local") return; // Don't ship dev errors to Sentry.

  Sentry.init({
    dsn: DSN,
    environment,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      const original = hint?.originalException;
      const message =
        (typeof original === "object" && original !== null && "message" in original &&
          typeof (original as { message: unknown }).message === "string"
          ? (original as { message: string }).message
          : event.message) ?? "";
      if (NOISY_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment))) {
        return null;
      }
      return event;
    },
  });

  initialized = true;
};

/** Identify the current user by UUID only (no email, no name). */
export const setSentryUser = (userId: string | null): void => {
  if (!initialized) return;
  if (userId) Sentry.setUser({ id: userId });
  else Sentry.setUser(null);
};

/** Manually report an exception (used by ErrorBoundary). */
export const reportException = (error: unknown, context?: Record<string, unknown>): void => {
  if (!initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
};

export { Sentry };
